import { Fragment, memo, useEffect, useRef } from "react";
import { Alert, Box, Chip, Stack, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import CheckIcon from "@mui/icons-material/CheckRounded";
import DescriptionIcon from "@mui/icons-material/DescriptionOutlined";
import { FoodsIcon } from "@foodpilot/foods";
import { useTranslation } from "react-i18next";
import { CONSENT_CATEGORY_CONFIG } from "./consentCategoryConfig";
import {
    type ConsentCreationState,
    type ConsentDraftLabel,
    CONSENT_CREATION_STEP_KEYS,
    getSummaryReadyMessage,
    getSummaryWarning,
    getTypeLabel,
} from "./consentCreationShared";

type StepperProps = {
    activeStep: number;
    maxAccessibleStep: number;
    onStepClick: (step: number) => void;
};

type SummaryStepProps = {
    completedLocaleCodes: string[];
    state: ConsentCreationState;
    summaryLabelDraft: ConsentDraftLabel;
};

const ConsentCreationStepperComponent = ({ activeStep, maxAccessibleStep, onStepClick }: StepperProps) => {
    const theme = useTheme();
    const { t, i18n } = useTranslation();
    const isFrench = (i18n.resolvedLanguage || i18n.language || "fr").startsWith("fr");
    const accentColor = theme.palette.primary.main;
    const previousStepRef = useRef(activeStep);
    const previousStep = previousStepRef.current;
    const stepFallbacks =
        isFrench ?
            ["Type & category", "Labels", "Initial version", "Summary"]
        :   ["Type & category", "Labels", "Initial version", "Summary"];

    useEffect(() => {
        previousStepRef.current = activeStep;
    }, [activeStep]);
    const stepItems = CONSENT_CREATION_STEP_KEYS.map((stepKey, index) => {
        const isCompleted = index < activeStep;
        const isActive = index === activeStep;
        const isFirst = index === 0;
        const isLast = index === CONSENT_CREATION_STEP_KEYS.length - 1;
        const isStepClickable = index <= maxAccessibleStep;
        const connectorActive = activeStep > index;
        const connectorJustActivated = activeStep > index && previousStep <= index;
        const connectorJustDeactivated = activeStep <= index && previousStep > index;

        return {
            connectorActive,
            connectorJustActivated,
            connectorJustDeactivated,
            isActive,
            isCompleted,
            isFirst,
            isLast,
            isStepClickable,
            stepKey,
        };
    });

    return (
        <Box
            sx={{
                px: { xs: 3, md: 5 },
                py: { xs: 2.5, md: 3 },
                borderBottom: `1px solid ${theme.custom.grey[500]}`,
                bgcolor: theme.palette.grey[100],
                flexShrink: 0,
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    width: "100%",
                    mb: 1.5,
                }}
            >
                {stepItems.map((stepItem, index) => {
                    const {
                        connectorActive,
                        connectorJustActivated,
                        connectorJustDeactivated,
                        isActive,
                        isCompleted,
                        isStepClickable,
                    } = stepItem;
                    const triggerStepSelection = () => {
                        if (!isStepClickable) {
                            return;
                        }

                        onStepClick(index);
                    };
                    const connectorAnimation =
                        connectorJustActivated ? "consent-connector-forward 260ms ease-out forwards"
                        : connectorJustDeactivated ? "consent-connector-backward 260ms ease-out forwards"
                        : "none";

                    return (
                        <Fragment key={stepItem.stepKey}>
                            <Box
                                onClick={triggerStepSelection}
                                role="button"
                                tabIndex={isStepClickable ? 0 : -1}
                                onKeyDown={(event) => {
                                    if (!isStepClickable) {
                                        return;
                                    }

                                    if (event.key === "Enter" || event.key === " ") {
                                        event.preventDefault();
                                        triggerStepSelection();
                                    }
                                }}
                                sx={{
                                    width: 34,
                                    height: 34,
                                    borderRadius: "50%",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    bgcolor:
                                        isCompleted ? theme.palette.success.main
                                        : isActive ? accentColor
                                        : theme.palette.grey[200],
                                    color:
                                        isCompleted || isActive ? theme.palette.common.white : theme.custom.grey[1400],
                                    fontWeight: 700,
                                    fontSize: theme.typography.body.fontSize,
                                    flexShrink: 0,
                                    cursor: isStepClickable ? "pointer" : "default",
                                    transition: "transform 120ms ease",
                                    "&:hover": {
                                        transform: isStepClickable ? "translateY(-1px)" : "none",
                                    },
                                }}
                            >
                                {isCompleted ?
                                    <CheckIcon sx={{ fontSize: theme.typography.body.fontSize }} />
                                :   index + 1}
                            </Box>
                            {index < CONSENT_CREATION_STEP_KEYS.length - 1 && (
                                <Box
                                    sx={{
                                        position: "relative",
                                        flex: 1,
                                        minWidth: 24,
                                        height: 2,
                                        borderRadius: 999,
                                        bgcolor: theme.palette.grey[400],
                                        mx: 1.5,
                                        overflow: "hidden",
                                    }}
                                >
                                    <Box
                                        sx={{
                                            position: "absolute",
                                            inset: 0,
                                            borderRadius: 999,
                                            bgcolor: alpha(accentColor, 0.38),
                                            clipPath: connectorActive ? "inset(0 0 0 0)" : "inset(0 100% 0 0)",
                                            animation: connectorAnimation,
                                            "@keyframes consent-connector-forward": {
                                                from: { clipPath: "inset(0 100% 0 0)" },
                                                to: { clipPath: "inset(0 0 0 0)" },
                                            },
                                            "@keyframes consent-connector-backward": {
                                                from: { clipPath: "inset(0 0 0 0)" },
                                                to: { clipPath: "inset(0 0 0 100%)" },
                                            },
                                        }}
                                    />
                                </Box>
                            )}
                        </Fragment>
                    );
                })}
            </Box>
            <Box
                sx={{
                    display: "flex",
                    alignItems: "flex-start",
                }}
            >
                {stepItems.map((stepItem, index) => {
                    const { isActive, isCompleted, isFirst, isLast, isStepClickable, stepKey } = stepItem;
                    const triggerStepSelection = () => {
                        if (!isStepClickable) {
                            return;
                        }

                        onStepClick(index);
                    };

                    return (
                        <Fragment key={`${stepKey}-label`}>
                            <Box sx={{ width: 34, position: "relative", height: 28, flexShrink: 0 }}>
                                <Typography
                                    variant="body"
                                    onClick={triggerStepSelection}
                                    sx={{
                                        position: "absolute",
                                        top: 0,
                                        left:
                                            isFirst ? 0
                                            : isLast ? "100%"
                                            : "50%",
                                        transform:
                                            isFirst ? "none"
                                            : isLast ? "translateX(-100%)"
                                            : "translateX(-50%)",
                                        fontWeight: isActive ? 700 : 500,
                                        color:
                                            isActive || isCompleted ?
                                                theme.palette.text.primary
                                            :   theme.custom.grey[2000],
                                        textAlign:
                                            isFirst ? "left"
                                            : isLast ? "right"
                                            : "center",
                                        fontSize: theme.typography.body.fontSize,
                                        cursor: isStepClickable ? "pointer" : "default",
                                        whiteSpace: "nowrap",
                                    }}
                                >
                                    {t(stepKey, stepFallbacks[index])}
                                </Typography>
                            </Box>
                            {index < CONSENT_CREATION_STEP_KEYS.length - 1 && (
                                <Box sx={{ flex: 1, minWidth: 24, mx: 1.5 }} />
                            )}
                        </Fragment>
                    );
                })}
            </Box>
        </Box>
    );
};

export const ConsentCreationStepper = memo(ConsentCreationStepperComponent);
ConsentCreationStepper.displayName = "ConsentCreationStepper";

export const ConsentSummaryStep = ({ completedLocaleCodes, state, summaryLabelDraft }: SummaryStepProps) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const selectedCategoryConfig = state.category ? CONSENT_CATEGORY_CONFIG[state.category] : null;
    const accentColor = theme.palette.primary.main;

    return (
        <Stack gap={3}>
            <Alert icon={<CheckIcon fontSize="inherit" />} severity="success" sx={{ borderRadius: 3 }}>
                {getSummaryReadyMessage(state.type, t)}
            </Alert>
            <Box sx={{ p: 3, borderRadius: 3, border: `2px solid ${accentColor}`, bgcolor: alpha(accentColor, 0.05) }}>
                <Stack direction="row" gap={2} alignItems="center">
                    <Box
                        sx={{
                            width: 52,
                            height: 52,
                            borderRadius: 2,
                            bgcolor: theme.palette.common.white,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                            color: accentColor,
                        }}
                    >
                        {selectedCategoryConfig ?
                            <FoodsIcon icon={selectedCategoryConfig.icon} size={2} />
                        :   <DescriptionIcon sx={{ color: accentColor }} />}
                    </Box>
                    <Stack gap={0.75}>
                        <Typography variant="body" sx={{ fontWeight: 700 }}>
                            {summaryLabelDraft.name.trim() ||
                                t("settings.consent.document_wizard.untitled", "Untitled")}
                        </Typography>
                        <Stack direction="row" gap={1} flexWrap="wrap">
                            {selectedCategoryConfig && (
                                <Chip
                                    label={t(selectedCategoryConfig.labelKey, selectedCategoryConfig.labelFallback)}
                                    size="small"
                                    sx={{
                                        bgcolor:
                                            selectedCategoryConfig.colorKey === "grey" ?
                                                theme.palette.grey[100]
                                            :   alpha(theme.palette[selectedCategoryConfig.colorKey].main, 0.1),
                                        color:
                                            selectedCategoryConfig.colorKey === "grey" ?
                                                theme.custom.grey[2000]
                                            :   theme.palette[selectedCategoryConfig.colorKey].main,
                                    }}
                                />
                            )}
                            <Chip
                                label={getTypeLabel(state.type, t)}
                                size="small"
                                sx={{
                                    bgcolor: alpha(accentColor, 0.1),
                                    color: accentColor,
                                    fontWeight: 600,
                                }}
                            />
                        </Stack>
                    </Stack>
                </Stack>
            </Box>
            <Box sx={{ border: `1px solid ${theme.custom.grey[500]}`, borderRadius: 3, overflow: "hidden" }}>
                {[
                    [t("settings.consent.document_wizard.summary.code", "Technical key"), state.code],
                    [t("settings.consent.document_wizard.summary.version", "Initial version"), `v${state.version}`],
                    [
                        t("settings.consent.document_wizard.summary.status", "Creation status"),
                        t("settings.consent.document_wizard.summary.status_active", "Active"),
                    ],
                    [
                        t("settings.consent.document_wizard.summary.languages", "Languages"),
                        completedLocaleCodes.map((code) => code.toUpperCase()).join(", ") || "—",
                    ],
                    [
                        t("settings.consent.document_wizard.summary.version_note", "Version note"),
                        state.versionNote.trim() || "—",
                    ],
                ].map(([label, value], index) => (
                    <Box
                        key={label}
                        sx={{
                            px: 3,
                            py: 2,
                            borderTop: index === 0 ? "none" : `1px solid ${theme.custom.grey[500]}`,
                            display: "flex",
                            justifyContent: "space-between",
                            gap: 2,
                        }}
                    >
                        <Typography variant="body2" sx={{ color: theme.custom.grey[2000] }}>
                            {label}
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600, textAlign: "right" }}>
                            {value}
                        </Typography>
                    </Box>
                ))}
            </Box>
            <Alert severity={state.type === "document" ? "warning" : "info"} sx={{ borderRadius: 3 }}>
                {getSummaryWarning(state.type, t)}
            </Alert>
        </Stack>
    );
};
